/* eslint-disable class-methods-use-this */
import {
  UserAgent,
  UserAgentState,
  Invitation,
  URI,
  Registerer,
  UserAgentOptions,
  Inviter,
  UserAgentDelegate,
  RegistererRegisterOptions,
  InviterOptions,
  Session,
  Core,
  RegistererState,
  TransportState,
} from 'sip.js';
import {
  DEFAULT_RING_TIMEOUT,
  EXPIRES,
  RETRY_TIMEOUT,
  REFRESH_FREQUENCY,
} from '../config/config';
import { log, formatUserAgent } from '../helpers';
import packageJson from '../../package.json';

export interface IPhoneOptions {
  userAgentOptions: {
    sipServer: string;
    username: string;
    password: string;
    ringTimeout: number;
    delegate: UserAgentDelegate;
    outgoingRequestDelegate: Core.OutgoingRequestDelegate;
  };
  registererOptions: {
    // eslint-disable-next-line
    registererState: (state: RegistererState) => void;
    registerOptions: RegistererRegisterOptions;
  };
}

class Phone {
  uac: UserAgent;

  registerer: Registerer;

  delegate: UserAgentDelegate;

  options: IPhoneOptions;

  offline: any;

  sipServer: string;

  constructor(options: IPhoneOptions) {
    this.options = options;

    const {
      sipServer,
      username,
      password,
      delegate,
    } = options.userAgentOptions;

    if (!sipServer || !username || !password)
      throw new Error(
        'You MUST provide the username, password and the sipServer url.'
      );

    this.sipServer = sipServer;

    const uri = this.makeURI(username);

    if (!uri) throw new Error('Failed to make the SIP URI.');

    this.uac = new UserAgent({
      uri,
      displayName: username,
      authorizationUsername: username,
      authorizationPassword: password,
      transportOptions: {
        server: `wss://${sipServer}:443`,
        keepAliveInterval: 60,
        keepAliveDebounce: 15,
      },
      delegate,
      noAnswerTimeout: DEFAULT_RING_TIMEOUT,
      allowLegacyNotifications: true,
      userAgentString: formatUserAgent(
        navigator.userAgent,
        packageJson.version
      ),
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionOptions: {
          rtcConfiguration: {
            iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
          },
          iceCheckingTimeout: 1000,
        },
      },
    });

    this.delegate = delegate;

    this.registerer = new Registerer(this.uac, {
      expires: EXPIRES,
      refreshFrequency: REFRESH_FREQUENCY,
    });

    // Monitor the Internet status.
    this.offline = () => {
      try {
        this.uac.transport.disconnect();
      } catch (err) {
        log.error('Failed to disconnected transport. Connect aborted.');
      }
    };

    window.addEventListener('offline', this.offline);
  }

  async handleRegistration() {
    if (this.registerer) {
      await this.registerer.dispose();
      // NOTE: If we dispose a Registerer then we MUST instantiate a new one.
      this.registerer = new Registerer(this.uac, {
        expires: EXPIRES,
        refreshFrequency: REFRESH_FREQUENCY,
      });
    }

    this.registerer.stateChange.addListener(
      this.options.registererOptions.registererState
    );

    // Send REGISTER to the SIP server.
    await this.registerer.register(
      this.options.registererOptions.registerOptions
    );
  }

  /**
   * @name hangUp
   * @description Hang up a call.
   * @returns {void}
   */
  async hangUp(session: Invitation) {
    await session.reject();
  }

  /**
   * @name attemptReconnection
   * @description Try to reconnect the UA Transport.
   * @returns {void}
   */
  attemptReconnection() {
    if (this.isConnected()) return;

    setTimeout(async () => {
      try {
        log.warn('Trying to reconnect.');
        if (this.uac.transport.state === TransportState.Disconnected) {
          await this.uac.reconnect();
        } else {
          throw new Error(
            'The Transport state is diferrent from disconnected.'
          );
        }
      } catch (err) {
        log.error(
          `NUACOM_LOG: Failure to reconnect, trying again. Reason: `,
          err
        );
        if (this.uac.state !== UserAgentState.Stopped) {
          this.attemptReconnection();
        }
      }
    }, RETRY_TIMEOUT * 1000);
  }

  /**
   * @name start
   * @description Start the UA Transport.
   * @returns {Phone}
   */
  async start() {
    try {
      await this.uac.start();
    } catch (err) {
      log.error('NUACOM_LOG: Failure to create the UA Transport.');
      this.attemptReconnection();
    }
  }

  /**
   * @name stop
   * @description Stop the UA Transport and do SIP Unregistering.
   * @returns {Promise}
   */
  async stop() {
    // Disable the event that monitor the Internet status.
    window.removeEventListener('offline', this.offline);
    await this.uac.stop();
  }

  /**
   * @name isConnected
   * @description Return the WS Transport status
   * @returns boolean
   */
  isConnected(): boolean {
    return this.uac.isConnected();
  }

  /**
   * @name isRegistered
   * @description Return the UA registration status
   * @returns {boolean}
   */
  isRegistered(): boolean {
    return this.registerer.state === 'Registered';
  }

  /**
   * @name isRegistered
   * @description Return the UA registration status
   * @returns {string}
   */
  getStatus(): string {
    return this.registerer.state;
  }

  /**
   * @name getUaConfiguration
   * @description Return the UA configuration options
   * @returns {UserAgentOptions}
   */
  getUaConfiguration(): UserAgentOptions {
    return this.uac.configuration;
  }

  /**
   * @name makeCall
   * @description Make a outbound call to a given number.
   * @param number string
   * @returns Invitation
   */
  makeCall(number: string, inviterOptions: InviterOptions): Session {
    const target = this.makeURI(number);

    if (!(target instanceof URI)) {
      throw new Error('You must provide a target phone number.');
    }
    const newSession = new Inviter(this.uac, target, inviterOptions);
    newSession.invite({
      requestDelegate: this.options.userAgentOptions.outgoingRequestDelegate,
    });

    return newSession;
  }

  /**
   * @name register()
   * @description UA Register
   */
  async register() {
    if (this.registerer.state !== RegistererState.Registered) {
      await this.handleRegistration();
    }
  }

  /**
   * @name unregister()
   * @description UA Unregister
   */
  async unregister() {
    return new Promise((resolve, reject) => {
      let attempts = 5;

      if (this.registerer.state !== RegistererState.Registered) {
        reject(new Error('REGISTER is already in progress.'));

        return;
      }

      this.registerer.unregister();

      const interval = setInterval(async () => {
        if (this.registerer.state === RegistererState.Unregistered) {
          resolve(true);
          clearInterval(interval);
        }

        if (attempts === 1) {
          reject(new Error('Exceeded the attempts to unregister the UA.'));
          clearInterval(interval);
        }

        attempts -= 1;
      }, 2000);
    });
  }

  /**
   * @name transportReconnect()
   * @description Try to reconnect the Transport
   */
  async transportReconnect(): Promise<void> {
    try {
      if (!this.uac.isConnected()) await this.uac.reconnect();
    } catch (err) {
      log.error(
        `transportReconnect(): Failure to reconnect the WebSocket.  Reason: `,
        err
      );
    }
  }

  /**
   * @name makeURI
   * @description Create a URI instance from a string
   * @param {string} target
   */
  makeURI(target: string): URI | undefined {
    const uri = UserAgent.makeURI(`sip:${target}@${this.sipServer}`);

    if (!(uri instanceof URI)) {
      throw new Error('Failure to create the URI instance.');
    }

    return uri;
  }

  /**
   * @name sendDTMF
   * @description Send dtmf signal to a given session
   * @returns {void}
   */
  sendDtmf(signal: string, session: Session): boolean {
    // Via SIP INFO
    // await session.info({
    //   requestOptions: {
    //     body: {
    //       contentDisposition: 'ender',
    //       contentType: 'application/dtmf-relay',
    //       content: `Signal=${signal}\r\nDuration=1000`,
    //     },
    //   },
    // });

    // Via RTP(RFC 4733)
    if (session && session.sessionDescriptionHandler) {
      return session.sessionDescriptionHandler.sendDtmf(signal);
    }

    return false;
  }

  /**
   * @name formatDtmfCode
   * @description Return a valid dtmf code
   * @returns {string}
   */
  formatDtmfCode(dialerCode: string): string {
    return String(dialerCode).replace(/[^0-9*#]+/, '');
  }
}

export default Phone;
